import { Card, Typography, Space, Tag, Tooltip, Button, message } from "antd";
import {
  ClockCircleOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  PlayCircleOutlined,
  Loading3QuartersOutlined,
  BankOutlined,
  ToolOutlined,
  FormOutlined,
  FileSearchOutlined,
  FieldTimeOutlined,
  AuditOutlined,
  CloseCircleOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import type { BankTaskComponentModel, Order } from "@services/api";
import { requestOrderStatus, fetchOrderModelByComponent } from "@services/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@app/authContext";

const { Title, Text } = Typography;

const copyableConfig = { tooltips: false } as const;

function formatDateTime(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  return new Intl.DateTimeFormat("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

// Умное обновление + определение "устарелости" (> 7 дней)
function useFreshness(iso?: string) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!iso) return;
    const ts = Date.parse(iso);
    if (Number.isNaN(ts)) return;

    const chooseInterval = () => {
      const diffMs = Date.now() - ts;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHours / 24);
      if (diffMin < 1) return 1000; // сек
      if (diffHours < 1) return 60_000; // мин
      if (diffDays < 1) return 3_600_000; // час
      return 0; // старше суток — можно не обновлять
    };

    const interval = chooseInterval();
    if (interval > 0) {
      const id = setInterval(() => setNow(Date.now()), interval);
      return () => clearInterval(id);
    }
  }, [iso, now]);

  return useMemo(() => {
    if (!iso) return null;
    const ts = Date.parse(iso);
    if (Number.isNaN(ts)) return null;

    const diffMs = now - ts;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    let label: string;
    if (diffMin < 1) label = "статус обновлён только что";
    else if (diffMin < 60) label = `статус обновлён ${diffMin} мин назад`;
    else if (diffHours < 24) label = `статус обновлён ${diffHours} ч назад`;
    else if (diffDays <= 7) label = `статус обновлён ${diffDays} дн назад`;
    else label = "статус обновлён давно";

    const isStale = diffDays > 7;
    return { label, isStale };
  }, [iso, now]);
}

// палитра — насыщенные оттенки в духе AntD
const vividPalette = {
  grayBg: "#f0f0f0", grayText: "#262626", grayBorder: "#d9d9d9",
  blue: "#1677ff",
  gold: "#faad14",
  orange: "#fa8c16",
  red: "#f5222d",
  lime: "#a0d911",
  green: "#52c41a",
};

// 1) Маппинг статуса → ключ палитры (логика сохранена)
export const fzStatusColorMap: Record<string, keyof typeof vividPalette | "grayBg"> = {
  started: "grayBg",              // серый
  inprogress: "grayBg",           // серый
  bgInBank: "blue",               // синий
  bgReworkAgent: "gold",          // жёлтый
  bgOnClientSigning: "orange",    // оранжевый
  bgReturnedFromClient: "gold",   // жёлтый
  bgClientTokenExpired: "gold",   // жёлтый
  acceptedForReview: "blue",      // синий
  reject: "red",                  // красный
  issuedInOtherBank: "lime",      // светло-зелёный
  executed: "green",              // зелёный
};

// 2) Базовый «пилюля»-стиль + тень
export const bigTagBaseStyle: React.CSSProperties = {
  alignSelf: "flex-start",
  fontSize: 16,
  padding: "6px 16px",
  borderRadius: 9999,
  lineHeight: 1.4,
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  borderWidth: 1,
  borderStyle: "solid",
};

// 3) Генератор стиля под статус
export function getBigTagStyleByStatus(status?: string): React.CSSProperties {
  const key = status ? fzStatusColorMap[status] : undefined;

  if (!key || key === "grayBg") {
    return {
      ...bigTagBaseStyle,
      backgroundColor: vividPalette.grayBg,
      color: vividPalette.grayText,
      borderColor: vividPalette.grayBorder,
    };
  }

  const bg = vividPalette[key as Exclude<typeof key, "grayBg">];
  const isLight = ["gold", "orange", "lime"].includes(key);
  return {
    ...bigTagBaseStyle,
    backgroundColor: bg,
    borderColor: bg,
    color: isLight ? "#141414" : "#fff",
  };
}

// Переименованные статусы
const fzStatusTitleMap: Record<string, string> = {
  started: "Инициализация",
  inprogress: "В работе",
  bgInBank: "В банке",
  bgReworkAgent: "Доработать",
  bgOnClientSigning: "Подписать докменты",
  bgReturnedFromClient: "Проверить подписанные документы",
  bgClientTokenExpired: "Ссылка просрочена",
  acceptedForReview: "Принято на рассмотрение",
  reject: "Отказ",
  issuedInOtherBank: "Выдано в другом банке",
  executed: "Гарантия выдана",
};

// Иконки внутри бейджа по смыслу
const fzStatusIconMap: Record<string, React.ReactNode> = {
  started: <PlayCircleOutlined />,
  inprogress: <Loading3QuartersOutlined />,
  bgInBank: <BankOutlined />,
  bgReworkAgent: <ToolOutlined />,
  bgOnClientSigning: <FormOutlined />,
  bgReturnedFromClient: <FileSearchOutlined />,
  bgClientTokenExpired: <FieldTimeOutlined />,
  acceptedForReview: <AuditOutlined />,
  reject: <CloseCircleOutlined />,
  issuedInOtherBank: <ApartmentOutlined />,
  executed: <SafetyCertificateOutlined />,
};

interface Props {
  task?: BankTaskComponentModel;
  orderId: string;
  order: Order;
  onRefresh?: () => void; // коллбэк без аргументов
}

export default function BankTaskInfo({ task, orderId, order, onRefresh }: Props) {
  const { auth } = useAuthContext();
  const accessToken = auth?.access_token ?? "";

  const [refreshing, setRefreshing] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [waitFailed, setWaitFailed] = useState(false);
  const [overrideTask, setOverrideTask] = useState<BankTaskComponentModel | undefined>(undefined);
  const prevTsRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const attrs = (overrideTask ?? task)?.attributes;

  const status = attrs?.bankStatus || undefined;
  const comment =
    attrs?.bankStatusDescription && attrs.bankStatusDescription !== status
      ? attrs.bankStatusDescription
      : undefined;

  const freshness = useFreshness(attrs?.integrationStatusDate);
  const formattedDate = attrs?.integrationStatusDate
    ? formatDateTime(attrs.integrationStatusDate)
    : undefined;

  // Статус в ФЗ (для бейджа под номером заявки)
  const fzCode = order?.orderStatus?.code;
  const fzTitle = order?.orderStatus?.title || (fzCode ? fzStatusTitleMap[fzCode] : undefined);
  const fzIcon = fzCode ? fzStatusIconMap[fzCode] : undefined;

  // Признак: скрыть содержимое средней колонки при некоторых статусах ФЗ
  const hideMiddle =
    fzCode === "reject" || fzCode === "issuedInOtherBank" || fzCode === "executed";

  const notifyOnRefresh = () => {
    try { onRefresh?.(); } catch { /* no-op */ }
  };

  const handleRefresh = async (): Promise<boolean> => {
    if (refreshing || waiting) return false;
    if (!accessToken) {
      message.error("Нет accessToken");
      return false;
    }
    try {
      setRefreshing(true);
      setWaitFailed(false);
      const prevTs = attrs?.integrationStatusDate ? Date.parse(attrs.integrationStatusDate) : NaN;
      prevTsRef.current = Number.isNaN(prevTs) ? null : prevTs;

      await requestOrderStatus(orderId, accessToken);
    } catch {
      message.error("Не удалось запросить статус");
      setRefreshing(false);
      return false;
    }
    setRefreshing(false);

    setWaiting(true);
    let attempts = 0;
    let updated = false;

    while (mountedRef.current && attempts < 10) {
      attempts += 1;
      try {
        const model = await fetchOrderModelByComponent(orderId, "bankTask", accessToken);
        const newAttrs = model?.components?.bankTask?.attributes;
        const newIso = newAttrs?.integrationStatusDate;
        const newTs = newIso ? Date.parse(newIso) : NaN;

        if (
          newIso &&
          !Number.isNaN(newTs) &&
          (prevTsRef.current === null || (prevTsRef.current !== null && newTs > prevTsRef.current))
        ) {
          if (!mountedRef.current) return false;
          setWaiting(false);
          setWaitFailed(false);
          setOverrideTask({ attributes: newAttrs });
          message.success("Статус обновлён");
          updated = true;
          break;
        }
      } catch {
        if (!mountedRef.current) return false;
        setWaiting(false);
        setWaitFailed(true);
        return false;
      }
      await new Promise((r) => setTimeout(r, 3000));
    }

    if (!updated) {
      if (mountedRef.current) {
        setWaiting(false);
        setWaitFailed(true);
      }
    }

    return updated;
  };

  const handleLeftRefreshClick = async () => {
    const hasBankOrderId = Boolean(attrs?.bankOrderId);
    if (hasBankOrderId) {
      await handleRefresh();
      notifyOnRefresh();
    } else {
      notifyOnRefresh();
    }
  };

  // Бейдж свежести/ожидания рядом с текстом статуса банка
  let leftBadge: React.ReactNode = null;
  if (waiting) {
    leftBadge = <Tag icon={<ClockCircleOutlined />}>ожидаем обновления статуса</Tag>;
  } else if (waitFailed) {
    leftBadge = (
      <Tag style={{ background: "#fff2f0", color: "#cf1322", borderColor: "#ffa39e" }}>
        не удалось обновить статус
      </Tag>
    );
  } else if (freshness && formattedDate) {
    leftBadge = (
      <Tooltip title={formattedDate}>
        <Tag
          icon={<ClockCircleOutlined />}
          style={
            freshness.isStale
              ? { background: "#f5f5f5", color: "#595959", borderColor: "#d9d9d9" }
              : undefined
          }
        >
          {freshness.label}
        </Tag>
      </Tooltip>
    );
  }

  return (
    <Card
      bordered
      bodyStyle={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "stretch",
        gap: 32,
        padding: 20,
      }}
    >
      {/* Левая колонка: номер заявки + бейдж ФЗ + кнопка Обновить */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "center",
          gap: 8,
          marginRight: 40,
          whiteSpace: "nowrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SafetyCertificateOutlined style={{ color: "#1677ff", fontSize: 28 }} />
          <Title level={4} style={{ margin: 0, color: "#1677ff", lineHeight: 1 }}>
            Заявка №{" "}
            <Text copyable={copyableConfig} strong style={{ color: "#1677ff" }}>
              {order?.orderNumber}
            </Text>
          </Title>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {fzTitle && (
            <Tag icon={fzIcon} style={getBigTagStyleByStatus(fzCode)}>
              {fzTitle}
            </Tag>
          )}
          <Button
            type="default"
            size="small"
            icon={<ReloadOutlined spin={refreshing || waiting} />}
            loading={refreshing || waiting}
            onClick={handleLeftRefreshClick}
          >
            Обновить
          </Button>
        </div>
      </div>

      {/* Средняя колонка: скрываем полностью при reject/issuedInOtherBank/executed */}
      <div style={{ flex: 2, minWidth: 0 }}>
        {!hideMiddle && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              {attrs?.bankTaskName && (
                <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                  {attrs.bankTaskName}
                </Title>
              )}
            </div>

            <Space size={8} wrap>
              {status && <Text type="secondary">{status}</Text>}
              {status && comment && <Text type="secondary">· {comment}</Text>}
              {!status && comment && <Text type="secondary">{comment}</Text>}
              {leftBadge}
            </Space>
          </>
        )}
      </div>

      {/* Правая колонка: техническая информация */}
      <div style={{ flex: 1, textAlign: "right" }}>
        <Space direction="vertical" size={4} style={{ alignItems: "flex-end" }}>
          {attrs?.bankOrderNumber && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Номер в банке:{" "}
              <Text copyable={copyableConfig} strong>
                {attrs.bankOrderNumber}
              </Text>
            </Text>
          )}

          {attrs?.bankOrderId && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID в банке: <Text copyable={copyableConfig}>{attrs.bankOrderId}</Text>
            </Text>
          )}
        </Space>
      </div>
    </Card>
  );
}
